import { Base } from '@studiometa/js-toolkit';
import { animate, stagger } from 'motion';
import { toggleClass } from '@studiometa/js-toolkit/utils';

/**
 * @typedef {object} SliderTimerRef
 * @property {SVGElement} circle
 * @property {HTMLElement} container
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} progress
 * @property {HTMLElement} steps
 * @property {HTMLElement} time
 */

/**
 * @typedef {object} SliderTimerInterface
 * @property {object} $refs
 */

/**
 * Slider class
 */
export default class PreparationSliderTimer extends Base {
  static config = {
    name: 'SliderTimer',
    refs: ['circle', 'container', 'steps[]', 'progress', 'time', 'hours', 'minutes'],
  };

  /**
   * Animation duration
   */
  duration = 5;

  /**
   * Animation timeout
   */
  timeout = null;

  /**
   * Circle radius
   */
  radius = 0;

  /**
   * Circle perimeter
   */
  perimeter = 0;

  /**
   * Mounted
   */
  mounted() {
    this.setup();
  }

  /**
   * Resized
   */
  resized() {
    this.setup();
  }

  /**
   * Set dimensions and styles
   */
  setup() {
    // Set dimensions
    this.radius = 0.5 * this.$refs.circle.getBoundingClientRect().height;
    this.perimeter = this.$refs.progress.getTotalLength();

    // Set progress styles
    this.$refs.progress.style.strokeDasharray = `${this.perimeter}px`;
    this.$refs.progress.style.strokeDashoffset = this.perimeter;
    this.$refs.progress.style.opacity = 1;

    // Position steps
    this.$refs.steps.forEach((step, index, arr) => {
      const radians = Math.PI * (-(2 * index) / arr.length);

      step.style.left = `${this.radius * (1 + Math.cos(radians))}px`;
      step.style.top = `${this.radius * (1 - Math.sin(radians))}px`;
    });

    // Show steps buttons after setup
    animate(this.$refs.steps, { opacity: 1 }, { delay: stagger(0.1) });
  }

  /**
   * Animate steps change and setup next frame timeout
   * @param {number} index
   */
  goTo(index) {
    clearTimeout(this.timeout);

    // Animate the circle stroke
    const segmentLength = this.perimeter / this.$refs.steps.length;

    const startDashoffset = (this.$refs.steps.length - index) * segmentLength;
    const endDashoffset = Math.max(this.$refs.steps.length - index - 1, 1) * segmentLength;

    animate(
      this.$refs.progress,
      { strokeDashoffset: [startDashoffset, endDashoffset] },
      { duration: this.duration, easing: 'linear' }
    );

    // Toggle steps classes
    this.$refs.steps.forEach((step, stepIndex) => {
      toggleClass(step, 'is-active', stepIndex === index);
      toggleClass(step, 'bg-gold', stepIndex <= index);
    });

    // Animate hours and minutes
    const animations = [
      { el: this.$refs.hours, offsetY: `${-100 * index}%`, delay: 0, duration: 1.2 },
      { el: this.$refs.minutes, offsetY: `${100 * index}%`, delay: 0.2, duration: 0.9 },
    ];

    animations.forEach((animation) => {
      animate(
        animation.el,
        { transform: `translateY(${animation.offsetY})` },
        {
          easing: 'ease-in-out',
          duration: animation.duration,
          delay: animation.delay,
        }
      );
    });

    // Setup next frame timeout
    if (index < this.$refs.steps.length - 1) {
      this.timeout = setTimeout(() => this.$parent.goTo(index + 1), this.duration * 1000);
    }
  }

  /**
   * Handle steps click
   * @param {MouseEvent} _
   * @param {number} index
   */
  onStepsClick(_, index) {
    this.$parent.goTo(index);
  }
}
